import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  FileText,
  AlertTriangle,
  CheckCircle2,
  Clock,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronRight,
  Printer,
  ClipboardList,
  ArrowRightLeft,
  ArrowLeftRight,
  Package,
  Shield,
  Wrench,
  Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function PhaseSection({ title, icon: Icon, color, steps, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`phase-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <button
        className={`w-full flex items-center gap-3 px-4 py-3 text-left font-semibold text-sm ${color} transition-colors`}
        onClick={() => setOpen(!open)}
        data-testid={`phase-toggle-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className="flex-1">{title}</span>
        <Badge variant="outline" className="text-xs mr-2">{steps.length} steps</Badge>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {open && (
        <div className="divide-y">
          {steps.map((step, idx) => (
            <div key={idx} className="px-4 py-3 hover:bg-slate-50/50 transition-colors">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0 mt-0.5">
                  {step.step}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-slate-900">{step.action}</span>
                    {step.form && (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                        Form {step.form}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{step.detail}</p>
                  <div className="flex items-center gap-4 mt-1.5 text-[11px] text-slate-400">
                    <span>Owner: <span className="text-slate-600">{step.owner}</span></span>
                    {step.target_time && <span>Target: <span className="text-slate-600">{step.target_time}</span></span>}
                    {step.sla && <span>SLA: <span className="text-slate-600">{step.sla}</span></span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PaperForm({ form }) {
  return (
    <div
      className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-white print:border-solid"
      data-testid={`paper-form-${form.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-500" />
          <span className="font-semibold text-sm">{form.name}</span>
        </div>
        <Badge variant="outline" className="text-[10px] font-mono">{form.id}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {form.fields.map((field, idx) => (
          <div key={idx} className="border-b border-dotted border-slate-200 pb-1 text-xs text-slate-500">
            {field}: <span className="text-slate-300">_______________</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          className="text-xs h-7"
          onClick={() => window.print()}
          data-testid={`print-form-${form.id}`}
        >
          <Printer className="w-3 h-3 mr-1" /> Print
        </Button>
      </div>
    </div>
  );
}

export default function ManualWorkaroundsTab({ planId, orgId, subIndustry, departmentName, serviceLine, systemName, rto, criticality }) {
  const { api } = useAuth();
  const [workarounds, setWorkarounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [enhancing, setEnhancing] = useState(null); // workaround_id being enhanced
  const [expandedWorkaround, setExpandedWorkaround] = useState(null);

  const fetchWorkarounds = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`${API}/api/manual-workarounds/${planId}`);
      setWorkarounds(res.data.data || []);
      if (res.data.data?.length > 0) {
        setExpandedWorkaround(res.data.data[0].id);
      }
    } catch {
      // No workarounds yet
      setWorkarounds([]);
    } finally {
      setLoading(false);
    }
  }, [api, planId]);

  useEffect(() => {
    if (planId) fetchWorkarounds();
  }, [planId, fetchWorkarounds]);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      await api.post(`${API}/api/manual-workarounds/generate`, {
        plan_id: planId,
        org_id: orgId,
        department_name: departmentName || "General",
        service_line: serviceLine,
        system_name: systemName,
        sub_industry: subIndustry,
        rto: rto,
        criticality: criticality,
      });
      toast.success("Manual workaround SOP generated");
      await fetchWorkarounds();
    } catch (err) {
      toast.error("Failed to generate workaround");
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (workaroundId) => {
    try {
      await api.put(`${API}/api/manual-workarounds/${workaroundId}/approve`, {
        approved_by: "BCM Manager",
      });
      toast.success("Workaround approved");
      await fetchWorkarounds();
    } catch {
      toast.error("Failed to approve");
    }
  };

  const handleRegenerate = async (workaroundId) => {
    try {
      await api.post(`${API}/api/manual-workarounds/${workaroundId}/regenerate`);
      toast.success("Workaround regenerated");
      await fetchWorkarounds();
    } catch {
      toast.error("Failed to regenerate");
    }
  };

  const handleDelete = async (workaroundId) => {
    try {
      await api.delete(`${API}/api/manual-workarounds/${workaroundId}`);
      toast.success("Workaround deleted");
      await fetchWorkarounds();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleEnhanceAI = async (workaround) => {
    try {
      setEnhancing(workaround.id);
      toast.info("Enhancing with AI... This may take up to 60 seconds.");
      const res = await api.post(`${API}/api/manual-workarounds/enhance-ai`, {
        workaround_id: workaround.id,
        org_name: departmentName,
        department_name: workaround.department_name,
        service_line: workaround.service_line,
        system_name: workaround.system_name,
        sub_industry: workaround.sub_industry || subIndustry,
      });
      if (res.data.success) {
        toast.success("Workaround enhanced with AI-generated custom procedures");
        await fetchWorkarounds();
      } else {
        toast.error(res.data.error || "AI enhancement failed");
      }
    } catch (err) {
      toast.error("AI enhancement failed. Please try again.");
    } finally {
      setEnhancing(null);
    }
  };

  // Determine if this is a critical process that should auto-trigger
  const isCritical = criticality === "critical" || (rto && parseInt(rto) <= 4);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="workarounds-loading">
        <Loader2 className="w-6 h-6 animate-spin text-sky-600 mr-2" />
        <span className="text-sm text-slate-500">Loading manual workarounds...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="manual-workarounds-tab">
      {/* AI Trigger Banner */}
      {workarounds.length === 0 && (
        <div
          className={`rounded-lg p-4 border ${isCritical ? "bg-red-50 border-red-200" : "bg-sky-50 border-sky-200"}`}
          data-testid="workaround-trigger-banner"
        >
          <div className="flex items-start gap-3">
            <Sparkles className={`w-5 h-5 mt-0.5 ${isCritical ? "text-red-600" : "text-sky-600"}`} />
            <div className="flex-1">
              <h4 className={`font-semibold text-sm ${isCritical ? "text-red-800" : "text-sky-800"}`}>
                {isCritical ? "Critical Process — Manual Workaround Required" : "Manual Workaround Available"}
              </h4>
              <p className="text-xs text-slate-600 mt-1">
                {isCritical
                  ? "This process has an RTO of " + (rto || "<=4h") + ". A manual workaround SOP is required to ensure continuity when IT systems are down."
                  : "Generate an offline operation template for when this system is unavailable."}
              </p>
              <Button
                size="sm"
                className={`mt-3 ${isCritical ? "bg-red-600 hover:bg-red-700" : "bg-sky-600 hover:bg-sky-700"}`}
                onClick={handleGenerate}
                disabled={generating}
                data-testid="generate-workaround-btn"
              >
                {generating ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating...</>
                ) : (
                  <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generate Manual Workaround SOP</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Generated Workarounds */}
      {workarounds.map((w) => (
        <Card key={w.id} className="overflow-hidden" data-testid={`workaround-card-${w.id}`}>
          <CardHeader className="py-3 bg-slate-50 border-b">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-slate-600" />
                <CardTitle className="text-sm">{w.system_name}</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={w.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                  {w.status === "approved" ? (
                    <><CheckCircle2 className="w-3 h-3 mr-1" /> Approved</>
                  ) : (
                    <><Clock className="w-3 h-3 mr-1" /> AI Generated</>
                  )}
                </Badge>
                <Badge variant="outline" className="text-[10px]">RTO: {w.rto}</Badge>
                <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">
                  {w.criticality}
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
              <span>Dept: {w.department_name}</span>
              {w.service_line && <span>| Service: {w.service_line}</span>}
              <span>| Generated: {new Date(w.generated_at).toLocaleString()}</span>
            </div>
          </CardHeader>

          <CardContent className="p-4 space-y-4">
            {/* Action Buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              {w.status !== "approved" && (
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
                  onClick={() => handleApprove(w.id)}
                  data-testid={`approve-workaround-${w.id}`}
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Approve & Publish
                </Button>
              )}
              <Button
                size="sm"
                className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white h-7 text-xs"
                onClick={() => handleEnhanceAI(w)}
                disabled={enhancing === w.id}
                data-testid={`enhance-ai-workaround-${w.id}`}
              >
                {enhancing === w.id ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Enhancing with AI...</>
                ) : (
                  <><Sparkles className="w-3 h-3 mr-1" /> Enhance with AI</>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => handleRegenerate(w.id)}
                data-testid={`regenerate-workaround-${w.id}`}
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={() => handleDelete(w.id)}
                data-testid={`delete-workaround-${w.id}`}
              >
                Delete
              </Button>
              {w.source === "ai_enhanced_gpt4o" && (
                <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-[10px]">
                  <Sparkles className="w-2.5 h-2.5 mr-1" /> AI-Enhanced
                </Badge>
              )}
            </div>

            {/* 3 Phase SOPs */}
            <div className="space-y-3">
              <PhaseSection
                title="Phase A — Failover (Digital to Manual)"
                icon={ArrowRightLeft}
                color="bg-red-50 hover:bg-red-100 text-red-800"
                steps={w.failover_steps || []}
                defaultOpen={true}
              />
              <PhaseSection
                title="Phase B — Manual Operations"
                icon={ClipboardList}
                color="bg-amber-50 hover:bg-amber-100 text-amber-800"
                steps={w.manual_operations || []}
              />
              <PhaseSection
                title="Phase C — Failback (Manual to Digital)"
                icon={ArrowLeftRight}
                color="bg-emerald-50 hover:bg-emerald-100 text-emerald-800"
                steps={w.failback_steps || []}
              />
            </div>

            {/* Sidebar: Resources + Forms + Exercise */}
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              {/* Resources */}
              {w.resources?.length > 0 && (
                <Card className="border-slate-200" data-testid="workaround-resources">
                  <CardHeader className="py-2 px-3">
                    <CardTitle className="text-xs flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5 text-sky-600" /> Resource Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 pb-3 pt-0">
                    <div className="space-y-2">
                      {w.resources.map((r, idx) => (
                        <div key={idx} className="text-xs bg-slate-50 rounded p-2">
                          <div className="font-medium text-slate-700">{r.name}</div>
                          <div className="text-slate-500">{r.quantity} &middot; {r.location}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Exercise Integration */}
              {w.exercise_task && (
                <Card className="border-sky-200 bg-sky-50/50" data-testid="workaround-exercise">
                  <CardHeader className="py-2 px-3">
                    <CardTitle className="text-xs flex items-center gap-1.5 text-sky-800">
                      <Shield className="w-3.5 h-3.5" /> Exercise Integration
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 pb-3 pt-0">
                    <div className="text-xs space-y-1">
                      <p className="font-medium text-sky-900">{w.exercise_task.title}</p>
                      <p className="text-sky-700 leading-relaxed">{w.exercise_task.description}</p>
                      <Badge variant="outline" className="text-[10px] bg-white">
                        Duration: {w.exercise_task.duration}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Approval Status */}
              <Card className="border-slate-200" data-testid="workaround-approval-status">
                <CardHeader className="py-2 px-3">
                  <CardTitle className="text-xs flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Approval Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3 pt-0">
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      <span>AI Generated</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {w.status === "approved" ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      ) : (
                        <Clock className="w-3.5 h-3.5 text-amber-500" />
                      )}
                      <span>{w.status === "approved" ? "Clinical Review Complete" : "Clinical Review Pending"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {w.status === "approved" ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      ) : (
                        <Clock className="w-3.5 h-3.5 text-slate-300" />
                      )}
                      <span>{w.status === "approved" ? `Approved by ${w.approved_by}` : "Manager Approval Waiting"}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Paper Forms */}
            {w.forms?.length > 0 && (
              <div data-testid="workaround-forms-section">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Paper Form Templates
                </h4>
                <div className="grid md:grid-cols-2 gap-3">
                  {w.forms.map((form, idx) => (
                    <PaperForm key={idx} form={form} />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
